import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Confetti from 'react-confetti';
import { useAuth } from '../context/AuthContext';
import { draughtsAPI } from '../services/api';
import { draughtsDuelSocket } from '../services/socket';
import { formatSquare, getLegalMoves } from '../draughts/engine';
import { notifyPlayerTurn } from '../utils/turnAlerts';
import { buildInviteLink } from '../utils/inviteLinks';
import {
  getGlobalSoundEnabled,
  setGlobalSoundEnabled,
  subscribeToGlobalSoundPreference,
} from '../utils/soundPreferences';
import '../styles/Flappy.css';
import '../styles/Draughts.css';
import QuickChat from '../components/QuickChat';
import { getSocket } from '../services/socket';

function asNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatTimer(ms) {
  const safe = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function pieceMeta(piece) {
  if (!piece) return null;
  const lower = String(piece).toLowerCase();
  const color = lower === 'w' ? 'white' : 'black';
  const king = piece === 'W' || piece === 'B';
  return { color, king };
}

export default function DraughtsMatch() {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const { user, refreshUser } = useAuth();

  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showWinnerConfetti, setShowWinnerConfetti] = useState(false);
  const [lastMove, setLastMove] = useState(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [rematchCandidate, setrematchCandidate] = useState(null);
  const [rematchBusy, setrematchBusy] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return getGlobalSoundEnabled();
  });
  const [tick, setTick] = useState(Date.now());
  const lastServerOffsetRef = useRef(0);
  const lastTurnTokenRef = useRef('');
  const inviteJoinAttemptedRef = useRef(false);

  const meId = asNumber(user?.id);
  const isPendingMoveRef = useRef(false); // suppress poll during active move submission
  const myColor = useMemo(() => {
    if (!match || !meId) return null;
    if (asNumber(match.white_id) === meId) return 'w';
    if (asNumber(match.black_id) === meId) return 'b';
    return null;
  }, [match, meId]);

  const isMyTurn = match?.status === 'in_progress' && myColor && match.turn === myColor;

  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    setGlobalSoundEnabled(soundEnabled);
  }, [soundEnabled]);

  useEffect(() => subscribeToGlobalSoundPreference(setSoundEnabled), []);



  const effectiveServerNow = tick + (lastServerOffsetRef.current || 0);
  const turnStartedAt = match?.turn_started_at ? new Date(match.turn_started_at).getTime() : null;
  const turnTimeoutMs = asNumber(match?.turn_timeout_seconds) * 1000;
  const turnDeadlineMs = turnStartedAt && turnTimeoutMs > 0 ? turnStartedAt + turnTimeoutMs : null;
  const turnTimeLeftMs = turnDeadlineMs ? Math.max(0, turnDeadlineMs - effectiveServerNow) : null;

  const board = useMemo(() => {
    if (!Array.isArray(match?.board_state)) return [];
    return match.board_state;
  }, [match?.board_state]);

  const legalMoves = useMemo(() => {
    if (!board.length || !match?.status || match.status !== 'in_progress') return [];
    const forced = match?.must_continue_from ? String(match.must_continue_from) : null;
    
    let alreadyCap = [];
    try {
      if (match?.already_captured) {
        alreadyCap = typeof match.already_captured === 'string' 
          ? JSON.parse(match.already_captured) 
          : (Array.isArray(match.already_captured) ? match.already_captured : []);
      }
    } catch(e) {}

    return getLegalMoves(board, String(match.turn || 'w'), forced, alreadyCap);
  }, [board, match?.status, match?.turn, match?.must_continue_from, match?.already_captured]);

  const legalTargets = useMemo(() => {
    if (!selectedSquare) return [];
    return legalMoves.filter((m) => m.from === selectedSquare).map((m) => m.to);
  }, [legalMoves, selectedSquare]);

  const loadLastMove = useCallback(async () => {
    try {
      const movesRes = await draughtsAPI.getMoves(matchId);
      const moves = Array.isArray(movesRes?.data?.moves) ? movesRes.data.moves : [];
      const latest = moves.length ? moves[moves.length - 1] : null;
      const movePayload = latest?.move_json;
      const from = String(movePayload?.from || '').toLowerCase();
      const to = String(movePayload?.to || '').toLowerCase();
      setLastMove(from && to ? { from, to } : null);
    } catch {
      setLastMove(null);
    }
  }, [matchId]);

  const load = async () => {
    try {
      setError('');
      const mRes = await draughtsAPI.getMatch(matchId);
      const m = mRes.data;
      setMatch(m);
      setIsReconnecting(false);
      await loadLastMove();
      if (typeof m?.server_now_ms === 'number') {
        lastServerOffsetRef.current = m.server_now_ms - Date.now();
      }
    } catch (e) {
      if (!e?.response) {
        setIsReconnecting(true);
        return;
      }
      setError(e.response?.data?.error || 'Failed to load match');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => {
      // Don't poll while a move is in-flight — avoids snap-back from stale DB reads
      if (!isPendingMoveRef.current) void load();
    }, 1500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, loadLastMove]);

  useEffect(() => {
    if (!matchId) return;
    draughtsDuelSocket.join(matchId);

    const onUpdate = (payload = {}) => {
      const updated = payload?.match;
      if (!updated) return;
      if (String(updated.id) !== String(matchId)) return;
      setIsReconnecting(false);
      setMatch(updated);
      void loadLastMove();
      if (typeof updated?.server_now_ms === 'number') {
        lastServerOffsetRef.current = updated.server_now_ms - Date.now();
      }
    };

    draughtsDuelSocket.onMatchUpdate(onUpdate);

    // Synchronize Rematch
    const onRematchCreated = (payload = {}) => {
      if (Number(payload.originalMatchId) === Number(matchId)) {
        void load();
      }
    };

    const onRematchAccepted = (payload = {}) => {
      if (Number(payload.originalMatchId) === Number(matchId)) {
        navigate(`/draughts/duel/${payload.newMatchId}`);
      }
    };

    draughtsDuelSocket.onRematchCreated(onRematchCreated);
    draughtsDuelSocket.onRematchAccepted(onRematchAccepted);

    return () => {
      draughtsDuelSocket.removeListeners();
    };
  }, [matchId, loadLastMove, navigate]);

  useEffect(() => {
    const handleOffline = () => setIsReconnecting(true);
    const handleOnline = () => {
      setIsReconnecting(false);
      void load();
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    if (match?.status === 'completed') refreshUser?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.status]);

  useEffect(() => {
    if (!match?.id || !myColor) return;
    if (match.status !== 'in_progress') return;

    const turnToken = `${match.id}:${match.turn}:${match.turn_started_at || ''}`;

    if (!lastTurnTokenRef.current) {
      lastTurnTokenRef.current = turnToken;
      return;
    }

    if (turnToken === lastTurnTokenRef.current) return;

    lastTurnTokenRef.current = turnToken;
    if (match.turn === myColor) {
      notifyPlayerTurn('Draughts');
    }
  }, [match?.id, match?.status, match?.turn, match?.turn_started_at, myColor]);

  const isParticipant = !!(match && (asNumber(match.creator_id) === meId || asNumber(match.opponent_id) === meId));
  const canAccept = !!(match && match.status === 'pending' && asNumber(match.opponent_id) === meId && !match.accepted_at);
  const canStart = !!(match && match.status === 'pending' && asNumber(match.creator_id) === meId && match.opponent_id && match.accepted_at);
  const canCancel = !!(match && match.status === 'pending' && asNumber(match.creator_id) === meId);
  const canResign = !!(match && match.status === 'in_progress' && isParticipant);
  const canJoinOpenRoom = !!(match && match.status === 'pending' && !match.opponent_id && !isParticipant);
  const isRoomFullForViewer = !!(match && match.status === 'pending' && match.opponent_id && !isParticipant && !canAccept);
  const drawOfferedBy = asNumber(match?.draw_offered_by);
  const hasDrawOffer = !!drawOfferedBy;
  const isMyDrawOffer = hasDrawOffer && drawOfferedBy === meId;
  const isOpponentDrawOffer = hasDrawOffer && drawOfferedBy !== meId;
  const inviteLink = match?.id ? buildInviteLink(`/draughts/duel/${match.id}`) : '';

  const copyInviteLink = async () => {
    if (!inviteLink) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        window.prompt('Copy Draughts invite link:', inviteLink);
      }
    } catch {
      window.prompt('Copy Draughts invite link:', inviteLink);
    }
  };



  const winnerId = asNumber(match?.winner_id);
  const didIWin = Boolean(match?.status === 'completed' && winnerId && winnerId === meId);
  const didILose = Boolean(match?.status === 'completed' && winnerId && winnerId !== meId && isParticipant);
  const RematchOpponentId = useMemo(() => {
    if (!match || !meId) return null;
    if (asNumber(match.white_id) === meId) return asNumber(match.black_id);
    if (asNumber(match.black_id) === meId) return asNumber(match.white_id);
    return null;
  }, [match, meId]);
  const RematchMode = String(match?.mode || (asNumber(match?.wager) > 0 ? 'betting' : 'friendly'));
  const RematchWager = asNumber(match?.wager);
  const RematchTimeControlSeconds = asNumber(match?.turn_timeout_seconds) || 30;
  const whiteName = match?.white_username || match?.creator_username || 'White';
  const blackName = match?.black_username || match?.opponent_username || 'Black';
  
  const isFlipped = myColor === 'b';
  
  const topColor = isFlipped ? 'w' : 'b';
  const bottomColor = isFlipped ? 'b' : 'w';
  const topName = isFlipped ? whiteName : blackName;
  const bottomName = isFlipped ? blackName : whiteName;
  const topPiece = isFlipped ? '⚪' : '⚫';
  const bottomPiece = isFlipped ? '⚫' : '⚪';
  
  const topStreak = isFlipped ? asNumber(match?.white_streak) : asNumber(match?.black_streak);
  const bottomStreak = isFlipped ? asNumber(match?.black_streak) : asNumber(match?.white_streak);
  
  const topTimeouts = isFlipped ? asNumber(match?.white_timeouts) : asNumber(match?.black_timeouts);
  const bottomTimeouts = isFlipped ? asNumber(match?.black_timeouts) : asNumber(match?.white_timeouts);
  
  const topPlayerName = isReconnecting ? `Reconnecting... ${topName}` : topName;
  const bottomPlayerName = isReconnecting ? `Reconnecting... ${bottomName}` : bottomName;
  const turnTimeout = asNumber(match?.turn_timeout_seconds);
  const isCompleted = match?.status === 'completed';
  const isInProgress = match?.status === 'in_progress';
  const isPending = match?.status === 'pending';

  useEffect(() => {
    if (!didIWin) { setShowWinnerConfetti(false); return undefined; }
    setShowWinnerConfetti(true);
    const timer = window.setTimeout(() => setShowWinnerConfetti(false), 4500);
    return () => window.clearTimeout(timer);
  }, [didIWin, match?.id]);

  useEffect(() => {
    const tryAutoJoinFromInvite = async () => {
      if (!canJoinOpenRoom) return;
      if (inviteJoinAttemptedRef.current) return;
      inviteJoinAttemptedRef.current = true;

      try {
        setError('');
        await draughtsAPI.joinMatch(matchId);
        refreshUser?.();
        await load();
      } catch (e) {
        const msg = String(e?.response?.data?.error || '').toLowerCase();
        if (msg.includes('full') || msg.includes('joined') || e?.response?.status === 409) {
          setError('This room is full. You can watch the game live.');
        } else {
          setError(e.response?.data?.error || 'Unable to join this room');
        }
      }
    };

    void tryAutoJoinFromInvite();
  }, [canJoinOpenRoom, matchId, refreshUser]);

  useEffect(() => {
    let alive = true;

    const loadrematchCandidate = async () => {
      if (!isCompleted || !isParticipant || !RematchOpponentId) {
        if (alive) setrematchCandidate(null);
        return;
      }

      try {
        const res = await draughtsAPI.listChallenges();
        const items = Array.isArray(res?.data) ? res.data : [];

        const matchPair = [asNumber(match?.creator_id), asNumber(match?.opponent_id)].sort((a, b) => a - b).join(':');
        const found = items.find((item) => {
          if (!item || String(item.status) !== 'pending') return false;
          if (String(item.id) === String(match?.id)) return false;
          if (String(item.mode || '') !== RematchMode) return false;
          if (asNumber(item.wager) !== RematchWager) return false;
          if (asNumber(item.turn_timeout_seconds) !== RematchTimeControlSeconds) return false;
          const itemPair = [asNumber(item.creator_id), asNumber(item.opponent_id)].sort((a, b) => a - b).join(':');
          return itemPair === matchPair;
        });
        if (alive) setrematchCandidate(found);
      } catch {
        if (alive) setrematchCandidate(null);
      }
    };

    loadrematchCandidate();
    const id = setInterval(loadrematchCandidate, 3000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [isCompleted, isParticipant, RematchOpponentId, RematchMode, RematchWager, RematchTimeControlSeconds, match?.id, match?.creator_id, match?.opponent_id]);

  const handleRematchCreate = async () => {
    if (!RematchOpponentId) {
      setError('Rematch is only available after both players are assigned.');
      return;
    }

    try {
      setrematchBusy(true);
      setError('');
      await draughtsAPI.rematch(matchId);
      refreshUser?.();
      // Stay on this page — the rematch candidate will appear via polling.
      // The creator will see "Open Rematch" once the opponent accepts.
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create Rematch challenge');
    } finally {
      setrematchBusy(false);
    }
  };

  const handleRematchAccept = async () => {
    if (!rematchCandidate) {
      setError('No Rematch challenge available yet.');
      return;
    }

    try {
      setrematchBusy(true);
      setError('');

      if (asNumber(rematchCandidate.opponent_id) === meId && !rematchCandidate.accepted_at) {
        await draughtsAPI.acceptMatch(rematchCandidate.id, { originalMatchId: matchId });
      } else if (!rematchCandidate.opponent_id) {
        await draughtsAPI.joinMatch(rematchCandidate.id);
      }

      refreshUser?.();
      navigate(`/draughts/duel/${rematchCandidate.id}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to accept Rematch challenge');
    } finally {
      setrematchBusy(false);
    }
  };

  const handleRematchDecline = async () => {
    if (!rematchCandidate) {
      setError('No Rematch challenge available yet.');
      return;
    }

    try {
      setrematchBusy(true);
      setError('');
      await draughtsAPI.cancelMatch(rematchCandidate.id);
      setrematchCandidate(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to decline Rematch challenge');
    } finally {
      setrematchBusy(false);
    }
  };

  const onSquareClick = async (row, col) => {
    if (!match || match.status !== 'in_progress') return;
    if (!isMyTurn || submitting) return;

    const clicked = formatSquare(row, col);

    if (!selectedSquare) {
      const piece = board[row]?.[col];
      if (!piece) return;
      const color = String(piece).toLowerCase() === 'w' ? 'w' : 'b';
      if (color !== myColor) return;
      if (!legalMoves.some((m) => m.from === clicked)) return;
      setSelectedSquare(clicked);
      return;
    }

    if (clicked === selectedSquare) { setSelectedSquare(null); return; }

    if (!legalTargets.includes(clicked)) {
      const piece = board[row]?.[col];
      if (piece) {
        const color = String(piece).toLowerCase() === 'w' ? 'w' : 'b';
        if (color === myColor && legalMoves.some((m) => m.from === clicked)) {
          setSelectedSquare(clicked);
          return;
        }
      }
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      isPendingMoveRef.current = true;

      // --- OPTIMISTIC UPDATE START ---
      const newBoard = JSON.parse(JSON.stringify(board));
      // Find indices for fromSquare
      const fCol = selectedSquare.charCodeAt(0) - 97;
      const fRow = 10 - parseInt(selectedSquare.substring(1));
      const p = newBoard[fRow][fCol];
      
      newBoard[fRow][fCol] = null;
      newBoard[row][col] = p;

      // Handle Kinging
      if (p === 'w' && row === 0) newBoard[row][col] = 'W';
      if (p === 'b' && row === 9) newBoard[row][col] = 'B';

      // Handle jump capture (supports multi-distance for flying kings)
      if (Math.abs(row - fRow) >= 2) {
        const dr = row > fRow ? 1 : -1;
        const dc = col > fCol ? 1 : -1;
        let r = fRow + dr;
        let c = fCol + dc;
        while (r !== row && c !== col) {
          if (newBoard[r][c] !== null) {
            newBoard[r][c] = null;
            break;
          }
          r += dr;
          c += dc;
        }
      }

      const optimisticMatch = { 
        ...match, 
        board_state: newBoard,
      };
      setMatch(optimisticMatch);
      setSelectedSquare(null);
      // --- OPTIMISTIC UPDATE END ---

      const res = await draughtsAPI.submitMove(matchId, { from: selectedSquare, to: clicked });
      // Use the authoritative server response directly — no extra load() needed
      // (socket will also push draughts:match:update to both players)
      if (res.data?.match) {
        setMatch(res.data.match);
        if (typeof res.data.match.server_now_ms === 'number') {
          lastServerOffsetRef.current = res.data.match.server_now_ms - Date.now();
        }
        await loadLastMove();
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit move');
      setSelectedSquare(null);
      await load(); // sync back to real state on error
    } finally {
      setSubmitting(false);
      isPendingMoveRef.current = false;
    }
  };

  const canAcceptRematch = Boolean(
    rematchCandidate
    && asNumber(rematchCandidate.opponent_id) === meId
    && !rematchCandidate.accepted_at
  );

  const canCreateRematch = Boolean(isCompleted && isParticipant && !rematchCandidate);

  return (
    <div className="chess-match-page">
      {showWinnerConfetti && <Confetti recycle={false} numberOfPieces={220} />}



      {/* â”€â”€â”€â”€â”€ TOP BAR â”€â”€â”€â”€â”€ */}
      <header className="cm-topbar">
        <button className="cm-icon-btn" onClick={() => navigate('/draughts/lobby')} title="Lobby">
          <span>←</span>
        </button>
        <div className="cm-topbar-center">
          <span className="cm-topbar-title">Draughts #{matchId}</span>
          {match?.mode && <span className="cm-badge">{match.mode}</span>}
          {asNumber(match?.wager) > 0 && <span className="cm-badge cm-badge--gold">{match.wager} WP</span>}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            className="cm-icon-btn"
            onClick={() => setSoundEnabled((prev) => !prev)}
            title={soundEnabled ? 'Mute' : 'Unmute'}
          >
            <span>{soundEnabled ? '🔊' : '🔇'}</span>
          </button>
          <button className="cm-icon-btn" onClick={() => navigate('/dashboard')} title="Dashboard">
            <span>🏠</span>
          </button>
        </div>
      </header>

      {error && !isReconnecting && <div className="cm-toast cm-toast--error">{error}</div>}

      {/* â”€â”€â”€â”€â”€ TOP PLAYER BAR (Black) â”€â”€â”€â”€â”€ */}
      {/* ————— TOP PLAYER BAR ————— */}
      <div className={`cm-player-bar${match?.turn === topColor && isInProgress ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">{topPiece}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="cm-player-bar__name">{topPlayerName}</span>
            {topStreak >= 3 && (
              <span style={{ fontSize: 11 }}>🔥 {topStreak}</span>
            )}
          </div>
        </div>

        <div className={`cm-clock${match?.turn === topColor && isInProgress ? ' cm-clock--ticking' : ''}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <div>{match?.turn === topColor && turnTimeLeftMs !== null ? formatTimer(turnTimeLeftMs) : turnTimeout > 0 ? `${turnTimeout}s` : 'No time'}</div>
          {topTimeouts > 0 && <div style={{ fontSize: '11px', color: '#e11d48', marginTop: '2px', fontWeight: 600 }}>Strike {topTimeouts}/3</div>}
        </div>
      </div>

      {/* ————— BOARD ————— */}
      <div className="dm-board-wrap">
        {loading && <div className="cm-loading">Loading…</div>}
        {!loading && !!board.length && (
          <div className="draughts-board-wrap">
            <div className="draughts-board-with-labels">
              <span />
              <div className="draughts-board-labels-top">
                {(isFlipped ? ['j','i','h','g','f','e','d','c','b','a'] : ['a','b','c','d','e','f','g','h','i','j']).map(f => <span key={f}>{f}</span>)}
              </div>
              <span />

              <div className="draughts-board-labels-left">
                {(isFlipped ? Array.from({length:10},(_,i) => i+1) : Array.from({length:10},(_,i) => 10-i)).map(n => <span key={n}>{n}</span>)}
              </div>

              <div className="draughts-board">
                {Array.from({length: 10}).map((_, displayY) => {
                  const rIdx = isFlipped ? 9 - displayY : displayY;
                  return Array.from({length: 10}).map((_, displayX) => {
                    const cIdx = isFlipped ? 9 - displayX : displayX;
                    const square = formatSquare(rIdx, cIdx);
                    const isDark = (rIdx + cIdx) % 2 === 0;
                    const isisSelected = selectedSquare === square;
                    const isTarget = legalTargets.includes(square);
                    const isLast = lastMove && (lastMove.from === square || lastMove.to === square);
                    const pieceAtSquare = board[rIdx]?.[cIdx];
                    const meta = pieceMeta(pieceAtSquare);
                    const isMustFrom = match?.must_continue_from === square;
                    const isMovable = isMyTurn && meta && 
                                      ((myColor === 'w' && meta.color === 'white') || (myColor === 'b' && meta.color === 'black')) && 
                                      legalMoves.some((m) => m.from === square);

                    return (
                      <button
                        key={square}
                        type="button"
                        className={[
                          'draughts-square',
                          isDark ? 'draughts-square--dark' : 'draughts-square--light',
                          isisSelected ? 'draughts-square--selected' : '',
                          isTarget && match?.mode === 'friendly' ? 'draughts-square--target' : '',
                          isLast ? 'draughts-square--last' : '',
                          isMustFrom ? 'draughts-square--must' : '',
                        ].join(' ')}
                        onClick={() => onSquareClick(rIdx, cIdx)}
                        disabled={!isDark}
                      >
                        {meta && (
                          <span className={[
                            'draughts-piece',
                            `draughts-piece--${meta.color}`,
                            meta.king ? 'draughts-piece--king' : '',
                            isMovable && match?.mode === 'friendly' ? 'draughts-piece--movable' : '',
                            isMustFrom ? 'draughts-piece--must-active' : '',
                          ].join(' ')} />
                        )}
                      </button>
                    );
                  });
                })}
              </div>

              <div className="draughts-board-labels-right">
                {(isFlipped ? Array.from({length:10},(_,i) => i+1) : Array.from({length:10},(_,i) => 10-i)).map(n => <span key={n}>{n}</span>)}
              </div>

              <span />
              <div className="draughts-board-labels-bottom">
                {(isFlipped ? ['j','i','h','g','f','e','d','c','b','a'] : ['a','b','c','d','e','f','g','h','i','j']).map(f => <span key={f}>{f}</span>)}
              </div>
              <span />
            </div>
          </div>
        )}
      </div>

      {/* ————— BOTTOM PLAYER BAR ————— */}
      <div className={`cm-player-bar${match?.turn === bottomColor && isInProgress ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">{bottomPiece}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="cm-player-bar__name">{bottomPlayerName}</span>
            {bottomStreak >= 3 && (
              <span style={{ fontSize: 11 }}>🔥 {bottomStreak}</span>
            )}
          </div>
        </div>

        <div className={`cm-clock${match?.turn === bottomColor && isInProgress ? ' cm-clock--ticking' : ''}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <div>{match?.turn === bottomColor && turnTimeLeftMs !== null ? formatTimer(turnTimeLeftMs) : turnTimeout > 0 ? `${turnTimeout}s` : 'No time'}</div>
          {bottomTimeouts > 0 && <div style={{ fontSize: '11px', color: '#e11d48', marginTop: '2px', fontWeight: 600 }}>Strike {bottomTimeouts}/3</div>}
        </div>
      </div>

      {/* ————— RESULT BANNER ————— */}
      {isCompleted && (
        <div className={`cm-result-banner${didIWin ? ' cm-result-banner--win' : didILose ? ' cm-result-banner--loss' : ' cm-result-banner--draw'}`}>
          <span className="cm-result-banner__icon">{didIWin ? '🏆' : didILose ? '💔' : '🤝'}</span>
          <span className="cm-result-banner__text">
            {winnerId ? `Winner: ${winnerId === asNumber(match.white_id) ? whiteName : blackName}` : 'Draw'}
          </span>
          {asNumber(match?.wager) > 0 && (
            <span className="cm-result-banner__payout">
              Pot {asNumber(match.wager) * 2} • Charge {Number.isFinite(Number(match.charge_amount)) ? match.charge_amount : Math.floor(asNumber(match.wager) * 2 * 0.07)} • Net {Number.isFinite(Number(match.net_pot)) ? match.net_pot : Math.max(0, Math.floor(asNumber(match.wager) * 2 * 0.93))}
            </span>
          )}
        </div>
      )}

      {/* ————— STATUS LINE ————— */}
      {isInProgress && (
        <div className="cm-status-line">
          {isMyTurn ? '⚡ Your turn' : '⌛ Waiting for opponent…'}
          {submitting && ' — Sending move…'}
          {match?.must_continue_from && ` — Must continue from ${match.must_continue_from}`}
          {isMyDrawOffer && ' — Draw offer sent'}
          {isOpponentDrawOffer && ' — Opponent offered a draw'}
          {lastMove && ` — Last move: ${lastMove.from} → ${lastMove.to}`}
        </div>
      )}

      {isPending && (
        <div className="cm-status-line">
          Waiting for opponent
          {inviteLink && (
            <button className="cm-link-btn" onClick={copyInviteLink}>📋 Copy invite link</button>
          )}
        </div>
      )}

      {isRoomFullForViewer && (
        <div className="cm-status-line">Room full — watch mode</div>
      )}



      {/* â”€â”€â”€â”€â”€ ACTIONS â”€â”€â”€â”€â”€ */}
      <div className="cm-actions">
        {canAccept && (
          <button className="cm-btn cm-btn--primary" onClick={async () => {
            try { setError(''); await draughtsAPI.acceptMatch(matchId); refreshUser?.(); await load(); }
            catch (e) { setError(e.response?.data?.error || 'Failed to accept'); }
          }}>Accept Challenge</button>
        )}

        {canJoinOpenRoom && (
          <button className="cm-btn cm-btn--primary" onClick={async () => {
            try {
              setError('');
              await draughtsAPI.joinMatch(matchId);
              refreshUser?.();
              await load();
            } catch (e) {
              const msg = String(e?.response?.data?.error || '').toLowerCase();
              if (msg.includes('full') || msg.includes('joined') || e?.response?.status === 409) {
                setError('This room is full. You can watch the game live.');
              } else {
                setError(e.response?.data?.error || 'Failed to join room');
              }
            }
          }}>Join Challenge</button>
        )}

        {canCancel && (
          <button className="cm-btn cm-btn--ghost" onClick={async () => {
            try { setError(''); await draughtsAPI.cancelMatch(matchId); refreshUser?.(); await load(); }
            catch (e) { setError(e.response?.data?.error || 'Failed to cancel'); }
          }}>Cancel</button>
        )}

        {canResign && (
          <button className="cm-btn cm-btn--danger" onClick={async () => {
            try { setError(''); await draughtsAPI.resign(matchId); refreshUser?.(); await load(); }
            catch (e) { setError(e.response?.data?.error || 'Failed to resign'); }
          }}>Resign</button>
        )}

        {isInProgress && isParticipant && !hasDrawOffer && (
          <button className="cm-btn cm-btn--ghost" onClick={async () => {
            try { setError(''); await draughtsAPI.offerDraw(matchId); await load(); }
            catch (e) { setError(e.response?.data?.error || 'Failed to offer draw'); }
          }}>Offer Draw</button>
        )}

        {isInProgress && isParticipant && isOpponentDrawOffer && (
          <button className="cm-btn cm-btn--primary" onClick={async () => {
            try { setError(''); await draughtsAPI.acceptDraw(matchId); refreshUser?.(); await load(); }
            catch (e) { setError(e.response?.data?.error || 'Failed to accept draw'); }
          }}>Accept Draw</button>
        )}

        {isInProgress && isParticipant && isOpponentDrawOffer && (
          <button className="cm-btn cm-btn--ghost" onClick={async () => {
            try { setError(''); await draughtsAPI.declineDraw(matchId); await load(); }
            catch (e) { setError(e.response?.data?.error || 'Failed to decline draw'); }
          }}>Decline Draw</button>
        )}

        {isInProgress && turnTimeout > 0 && (
          <button className="cm-btn cm-btn--ghost" onClick={async () => {
            try { setError(''); await draughtsAPI.claimTimeout(matchId); refreshUser?.(); await load(); }
            catch (e) { setError(e.response?.data?.error || 'Failed to claim timeout'); }
          }}>⏲ Claim Timeout</button>
        )}

        {canCreateRematch && (
          <button
            className="cm-btn cm-btn--primary"
            disabled={rematchBusy}
            onClick={handleRematchCreate}
          >
            {rematchBusy ? 'Sending…' : `🔄 Rematch${RematchWager > 0 ? ` (${RematchWager})` : ''}`}
          </button>
        )}

        {canStart && (
          <button
            className="cm-btn cm-btn--primary"
            onClick={async () => {
              try {
                setError('');
                await draughtsAPI.startMatch(matchId);
                refreshUser?.();
                await load();
              } catch (e) {
                setError(e.response?.data?.error || 'Failed to start match');
              }
            }}
          >
            ▶ Start Match
          </button>
        )}

        {isCompleted && isParticipant && rematchCandidate && (
          <>
            <button
              className="cm-btn cm-btn--primary"
              disabled={rematchBusy}
              onClick={canAcceptRematch ? handleRematchAccept : () => navigate(`/draughts/duel/${rematchCandidate.id}`)}
            >
              {rematchBusy
                ? 'Wait…'
                : canAcceptRematch
                  ? `✅ Accept Rematch${asNumber(rematchCandidate.wager) > 0 ? ` (${asNumber(rematchCandidate.wager)})` : ''}`
                  : '🔄 Open Rematch'}
            </button>
            {canAcceptRematch && (
              <button
                className="cm-btn cm-btn--ghost"
                disabled={rematchBusy}
                onClick={handleRematchDecline}
              >
                Decline
              </button>
            )}
          </>
        )}

        {isCompleted && (
          <button className="cm-btn cm-btn--ghost" onClick={() => navigate('/draughts/lobby')}>
            Back to Lobby
          </button>
        )}
      </div>



      <QuickChat
        socket={draughtsDuelSocket.socket || (typeof getSocket === 'function' ? getSocket() : null)}
        roomId={matchId}
        meId={user?.id}
        gameType="draughts"
      />
    </div>
  );
}





